<template>
  <div class="mod-production-schedule">
    <el-tabs v-model="activeTab">
      <el-tab-pane label="工单计划" name="order" />
      <el-tab-pane label="甘特图" name="gantt" />
      <el-tab-pane label="机台负荷" name="load" />
    </el-tabs>
    <template v-if="activeTab === 'order'">
      <el-alert type="info" show-icon :closable="false" style="margin-bottom:12px"
        title="维护工单计划开工/完工日；自动排程需先维护工作中心/机台。排程起点会避开节假日；跨日任务和班次时间仍未完整处理。" />
      <el-form class="toolbar" :inline="true" @keyup.enter.native="search">
        <el-form-item label="快捷">
          <el-select v-model="q.scheduleScope" clearable size="small" style="width:130px" placeholder="全部" @change="search">
            <el-option label="本周须完工" value="week" />
            <el-option label="逾期未完工" value="overdue" />
            <el-option label="未排产" value="unscheduled" />
          </el-select>
        </el-form-item>
        <el-form-item label="工单号"><el-input v-model="q.productionOrderCode" size="small" clearable style="width:150px" /></el-form-item>
        <el-form-item label="产品编码"><el-input v-model="q.materialCode" size="small" clearable style="width:150px" /></el-form-item>
        <el-form-item>
          <el-button v-if="isAuth('production:productionSchedule:list')" type="primary" size="small" @click="search">查询</el-button>
          <el-button size="small" @click="resetQuery">重置</el-button>
          <el-button v-if="isAuth('production:productionSchedule:save')" type="success" size="small" :disabled="!dirtyRows.length" :loading="saving" @click="saveAll">保存排产</el-button>
        </el-form-item>
      </el-form>
      <el-table :data="pageList" border v-loading="loading" :header-cell-style="headStyle" @row-dblclick="viewOrder">
        <el-table-column type="index" label="序号" width="50" header-align="center" align="center" />
        <el-table-column prop="productionOrderCode" label="工单号" min-width="130" header-align="center" align="left" show-overflow-tooltip>
          <template slot-scope="s">
            <a v-if="isAuth('production:productionOrder:info')" class="code-link" @click="viewOrder(s.row)">{{ s.row.productionOrderCode }}</a>
            <span v-else>{{ s.row.productionOrderCode }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="materialCode" label="产品编码" min-width="110" header-align="center" align="left" show-overflow-tooltip />
        <el-table-column prop="materialName" label="产品名称" min-width="120" header-align="center" align="left" show-overflow-tooltip />
        <el-table-column prop="quantity" label="计划量" width="90" header-align="center" align="left" />
        <el-table-column prop="receivedQty" label="已入库" width="90" header-align="center" align="left">
          <template slot-scope="s">{{ formatQty(s.row.receivedQty) }}</template>
        </el-table-column>
        <el-table-column prop="pendingReceiptQty" label="待入库" width="90" header-align="center" align="left">
          <template slot-scope="s">{{ formatQty(s.row.pendingReceiptQty) }}</template>
        </el-table-column>
        <el-table-column label="计划开工" width="160" header-align="center" align="left">
          <template slot-scope="s">
            <el-date-picker v-model="s.row.planStartDate" type="date" value-format="yyyy-MM-dd HH:mm:ss" size="mini" style="width:140px"
              :disabled="!canEditRow(s.row)" @change="markDirty(s.row)" />
          </template>
        </el-table-column>
        <el-table-column label="计划完工" width="160" header-align="center" align="left">
          <template slot-scope="s">
            <el-date-picker v-model="s.row.planFinishDate" type="date" value-format="yyyy-MM-dd HH:mm:ss" size="mini" style="width:140px"
              :disabled="!canEditRow(s.row)" @change="markDirty(s.row)" />
          </template>
        </el-table-column>
        <el-table-column prop="salesOrderSummary" label="销售订单" min-width="110" header-align="center" align="left" show-overflow-tooltip />
        <el-table-column prop="orderStatus" label="状态" width="80" header-align="center" align="center">
          <template slot-scope="s">
            <el-tag v-if="s.row.orderStatus === '1'" type="primary" size="mini">已下达</el-tag>
            <el-tag v-else-if="s.row.orderStatus === '3'" type="success" size="mini">已完工</el-tag>
            <el-tag v-else type="warning" size="mini">草稿</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="逾期" width="70" header-align="center" align="center">
          <template slot-scope="s">
            <el-tag v-if="isOverdue(s.row)" type="danger" size="mini">逾期</el-tag>
            <span v-else>—</span>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination class="list-pagination" background :current-page="pageIndex" :page-sizes="[20, 50, 100]" :page-size="pageSize" :total="total"
        layout="total, sizes, prev, pager, next, jumper" @size-change="s => { pageSize = s; pageIndex = 1 }" @current-change="p => { pageIndex = p }" />
    </template>
    <production-schedule-gantt v-else-if="activeTab === 'gantt'" @scheduled="load" />
    <production-schedule-load v-else />
  </div>
</template>

<script>
import request from '../../api/request'
import { fetchPerms } from '../../api/auth'
import ProductionScheduleGantt from './production-schedule-gantt.vue'
import ProductionScheduleLoad from './production-schedule-load.vue'

export default {
  name: 'ProductionSchedule',
  components: { ProductionScheduleGantt, ProductionScheduleLoad },
  data() {
    return {
      activeTab: 'order',
      q: { productionOrderCode: '', materialCode: '', scheduleScope: '' },
      list: [], pageIndex: 1, pageSize: 20, loading: false, saving: false, permCodes: [],
      dirtyMap: {},
      headStyle: { background: '#f5f7fa', color: '#606266' }
    }
  },
  computed: {
    filtered() {
      const now = new Date()
      const weekStart = this.weekStartDate(now)
      const weekEnd = this.weekEndDate(now)
      return this.list.filter(row => {
        const code = (this.q.productionOrderCode || '').trim()
        const material = (this.q.materialCode || '').trim()
        if (code && String(row.productionOrderCode || '').indexOf(code) < 0) return false
        if (material && String(row.materialCode || '').indexOf(material) < 0) return false
        const scope = this.q.scheduleScope
        if (scope === 'unscheduled') {
          if (row.planStartDate && row.planFinishDate) return false
        } else if (scope === 'overdue') {
          if (!this.isOverdue(row)) return false
        } else if (scope === 'week') {
          if (!row.planFinishDate) return false
          const finish = new Date(String(row.planFinishDate).replace(/-/g, '/'))
          if (Number.isNaN(finish.getTime()) || finish < weekStart || finish > weekEnd || String(row.orderStatus) === '3') return false
        }
        return true
      })
    },
    total() { return this.filtered.length },
    pageList() { return this.filtered.slice((this.pageIndex - 1) * this.pageSize, this.pageIndex * this.pageSize) },
    dirtyRows() {
      return this.list.filter(row => this.dirtyMap[row.productionOrderId])
    }
  },
  created() { this.loadPerms(); this.load() },
  methods: {
    isAuth(perm) {
      const user = this.$store.state.user || {}
      const codes = Array.isArray(user.roleCodes) ? user.roleCodes : []
      if (['TENANT_ADMIN', 'PLATFORM_ADMIN'].indexOf(String(user.roleCode || '')) >= 0 || codes.indexOf('TENANT_ADMIN') >= 0 || codes.indexOf('PLATFORM_ADMIN') >= 0) return true
      return this.permCodes.indexOf(perm) >= 0
    },
    loadPerms() { fetchPerms().then(res => { this.permCodes = res.data || [] }).catch(() => {}) },
    search() { this.pageIndex = 1; this.load() },
    resetQuery() {
      this.q = { productionOrderCode: '', materialCode: '', scheduleScope: '' }
      this.search()
    },
    weekEndDate(base) {
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate())
      const day = d.getDay() || 7
      d.setDate(d.getDate() + (7 - day))
      d.setHours(23, 59, 59, 999)
      return d
    },
    weekStartDate(base) {
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate())
      const day = d.getDay() || 7
      d.setDate(d.getDate() - (day - 1))
      return d
    },
    isOverdue(row) {
      if (!row || !row.planFinishDate || String(row.orderStatus) === '3') return false
      const finish = new Date(String(row.planFinishDate).replace(/-/g, '/'))
      if (Number.isNaN(finish.getTime())) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return finish < today
    },
    canEditRow(row) {
      const st = String(row.orderStatus || '')
      return st === '0' || st === '1'
    },
    markDirty(row) {
      if (!row || !row.productionOrderId) return
      this.$set(this.dirtyMap, row.productionOrderId, true)
    },
    formatQty(val) {
      if (val == null || val === '') return '—'
      const n = Number(val)
      if (Number.isNaN(n)) return '—'
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
    load() {
      this.loading = true
      this.dirtyMap = {}
      return request.get('/production/schedule/list', { params: { keyword: this.q.productionOrderCode } }).then(res => {
        this.list = (res.data || []).map(row => ({ ...row }))
      }).catch(() => { this.list = [] }).finally(() => { this.loading = false })
    },
    saveAll() {
      if (!this.dirtyRows.length) return
      this.saving = true
      const lines = this.dirtyRows.map(row => ({
        productionOrderId: row.productionOrderId,
        planStartDate: row.planStartDate || '',
        planFinishDate: row.planFinishDate || ''
      }))
      request.post('/production/schedule/save', { lines }).then(() => {
        this.$message.success('排产已保存')
        this.load()
      }).catch(() => {}).finally(() => { this.saving = false })
    },
    viewOrder(row) {
      if (!row || !row.productionOrderId || !this.isAuth('production:productionOrder:info')) return
      this.$router.push({ path: '/production/productionOrder', query: { viewId: row.productionOrderId } })
    }
  }
}
</script>

<style scoped>
.list-pagination { margin-top: 12px; text-align: right; }
.toolbar >>> .el-form-item { margin-bottom: 8px; }
.code-link { color: #348adb; cursor: pointer; }
</style>
